import { FilterChipType } from "@/components/search-filter/types";
import { VehicleType } from "@/features/search-filter/types/FilterEntitys";
import { formatDateString } from "@/lib/dates/formatters";
import { Currencies } from "@/redux/profile/reducers";
import { OptionItem } from "@/types/OptionItem";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import { SailingCategory } from "../../types/PriceListRow";

export const sectionIdConstants = {
  customer: "SELECTEDCUSTOMER",
  sailingCategory: "SELECTEDSAILINGCATEGORY",
  routes: "SELECTEDROUTES",
  vehicleTypes: "SELECTEDVEHICLETYPES",
  lengthRange: "SELECTEDLENGTHRANGE",
  date: "SELECTEDDATE",
  currency: "SELECTEDCURRENCY",
};

export interface LengthRange {
  from?: number;
  to?: number;
}

export interface PriceListFilterStateType {
  selectedCustomers: OptionItem[];
  selectedSailingCategories: SailingCategory[];
  selectedRoutes: OptionItem[];
  selectedVehicleTypes: VehicleType[];
  vehicleTypeAll: boolean;
  lengthRange: LengthRange;
  date: string;
  /**
   * The currency prices are converted into. The price list's own, not the
   * profile currency, so picking one here leaves the rest of the app alone.
   */
  currency: string;
}

export type PriceListFilterAction =
  | { type: "SET_CUSTOMERS"; payload: OptionItem[] }
  | { type: "TOGGLE_SAILING_CATEGORY"; payload: SailingCategory }
  | { type: "SET_ROUTES"; payload: OptionItem[] }
  | { type: "SET_VEHICLE_TYPES"; payload: VehicleType[] }
  | { type: "SET_VEHICLE_TYPE_ALL"; payload: boolean }
  | { type: "SET_LENGTH_RANGE"; payload: LengthRange }
  | { type: "SET_DATE"; payload: string }
  | { type: "SET_CURRENCY"; payload: string }
  | { type: "CLEAR" };

export interface PriceListFilterContextType {
  dispatch: React.Dispatch<PriceListFilterAction>;
  filterState: PriceListFilterStateType;
  filterIsOpen: boolean;
  setFilterIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clearFilter: () => void;
  selectedFilterChips: FilterChipType[];
  setSelectedFilterChips: React.Dispatch<
    React.SetStateAction<FilterChipType[]>
  >;
  openSectionIds: string[];
  toggleSectionId: (sectionId: string) => void;
  setOpenSectionIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const initialFilterState: PriceListFilterStateType = {
  selectedCustomers: [],
  selectedSailingCategories: [],
  selectedRoutes: [],
  selectedVehicleTypes: [],
  vehicleTypeAll: false,
  lengthRange: {},
  date: formatDateString(new Date()),
  currency: Currencies.EUR,
};

const filterReducer = (
  state: PriceListFilterStateType,
  action: PriceListFilterAction
): PriceListFilterStateType => {
  switch (action.type) {
    case "SET_CUSTOMERS":
      return { ...state, selectedCustomers: action.payload };
    case "TOGGLE_SAILING_CATEGORY": {
      const exists = state.selectedSailingCategories.includes(action.payload);
      return {
        ...state,
        selectedSailingCategories: exists
          ? state.selectedSailingCategories.filter((c) => c !== action.payload)
          : [...state.selectedSailingCategories, action.payload],
      };
    }
    case "SET_ROUTES":
      return { ...state, selectedRoutes: action.payload };
    case "SET_VEHICLE_TYPES":
      return { ...state, selectedVehicleTypes: action.payload };
    case "SET_VEHICLE_TYPE_ALL":
      return { ...state, vehicleTypeAll: action.payload };
    case "SET_LENGTH_RANGE":
      return { ...state, lengthRange: action.payload };
    case "SET_DATE":
      return { ...state, date: action.payload };
    case "SET_CURRENCY":
      return { ...state, currency: action.payload };
    case "CLEAR":
      return initialFilterState;
  }
};

export const getChipsFromFilterState = (
  filterState: PriceListFilterStateType
): FilterChipType[] => {
  const chips: FilterChipType[] = [];

  if (filterState.selectedCustomers.length > 0) {
    chips.push(
      ...filterState.selectedCustomers.map((customer) => ({
        label: customer.label,
        sectionId: sectionIdConstants.customer,
      }))
    );
  }

  if (filterState.selectedSailingCategories.length > 0) {
    chips.push(
      ...filterState.selectedSailingCategories.map((category) => ({
        label: category,
        sectionId: sectionIdConstants.sailingCategory,
      }))
    );
  }

  if (filterState.selectedRoutes.length > 0) {
    chips.push(
      ...filterState.selectedRoutes.map((route) => ({
        label: route.label,
        sectionId: sectionIdConstants.routes,
      }))
    );
  }

  if (filterState.vehicleTypeAll) {
    chips.push({
      label: "Vehicle type = ALL",
      sectionId: sectionIdConstants.vehicleTypes,
    });
  }

  if (filterState.selectedVehicleTypes.length > 0) {
    chips.push(
      ...filterState.selectedVehicleTypes.map((vehicleType) => ({
        label: vehicleType.name,
        sectionId: sectionIdConstants.vehicleTypes,
      }))
    );
  }

  const { from: lengthFrom, to: lengthTo } = filterState.lengthRange;
  if (lengthFrom !== undefined || lengthTo !== undefined) {
    chips.push({
      label: `Length ${lengthFrom ?? ""}-${lengthTo ?? ""} m`,
      sectionId: sectionIdConstants.lengthRange,
    });
  }

  if (filterState.date) {
    chips.push({
      label: filterState.date,
      sectionId: sectionIdConstants.date,
    });
  }

  if (filterState.currency) {
    chips.push({
      label: filterState.currency,
      sectionId: sectionIdConstants.currency,
    });
  }

  return chips;
};

export const PriceListFilterContext =
  createContext<PriceListFilterContextType | null>(null);

export const PriceListFilterProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [filterState, dispatch] = useReducer(filterReducer, initialFilterState);
  const [selectedFilterChips, setSelectedFilterChips] = useState<
    FilterChipType[]
  >([]);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);

  const clearFilter = useCallback(() => {
    dispatch({ type: "CLEAR" });
    setSelectedFilterChips([]);
    setOpenSectionIds([]);
  }, []);

  const toggleSectionId = useCallback(
    (sectionId: string) =>
      setOpenSectionIds((previousState) =>
        previousState.includes(sectionId)
          ? previousState.filter((item) => item !== sectionId)
          : [...previousState, sectionId]
      ),
    []
  );

  const contextValue = useMemo(
    () => ({
      filterState,
      dispatch,
      filterIsOpen,
      setFilterIsOpen,
      clearFilter,
      selectedFilterChips,
      setSelectedFilterChips,
      openSectionIds,
      toggleSectionId,
      setOpenSectionIds,
    }),
    [
      filterState,
      filterIsOpen,
      clearFilter,
      selectedFilterChips,
      openSectionIds,
      toggleSectionId,
    ]
  );

  return (
    <PriceListFilterContext.Provider value={contextValue}>
      {children}
    </PriceListFilterContext.Provider>
  );
};
